import { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Trash2 } from 'lucide-react';
import { useWorkoutStore } from '../stores/workoutStore';
import { useFeedbackStore } from '../stores/feedbackStore';
import { cn } from '../lib/utils';
import type { FeedbackRating } from '../lib/api';

interface WeekFeedbackProps {
  /** ISO date of the week's earliest day. Doubles as the week's anchor
   *  in the unique index (user_id, admin_sub, workout_date, scope='week'). */
  weekStart: string;
}

/**
 * Admin rating for the whole week's structure — day-to-day progression,
 * intensity arc, exercise rotation. Sits at the foot of WeekCalendar.
 *
 * One rating per admin per user per week (anchored on the week's start
 * date). Re-submitting updates the existing row. Server snapshots the
 * entire week dict so the rating remains useful as a post-training
 * sample even if the season is later edited.
 *
 * Visually distinct from DayFeedback (wider, captioned) so the admin
 * doesn't confuse rating a single day with rating the whole week.
 */
export function WeekFeedback({ weekStart }: WeekFeedbackProps) {
  const currentUser = useWorkoutStore((s) => s.currentUser);
  const userId = currentUser?.id;

  const { byWeekStart, pendingByKey, loadForUser, submitWeek, remove } = useFeedbackStore();
  const existing = byWeekStart[weekStart];
  const isPending = !!pendingByKey[`week:${weekStart}`];

  // Defensive — DayFeedback usually fires this first via the day cards,
  // but if the WeekCalendar were ever rendered without any DayCard
  // mounted (zero-day week), this ensures hydration still happens.
  useEffect(() => {
    if (userId) void loadForUser(userId);
  }, [userId, loadForUser]);

  const [expanded, setExpanded] = useState(false);
  const [draftRating, setDraftRating] = useState<FeedbackRating | null>(null);
  const [draftNotes, setDraftNotes] = useState('');

  useEffect(() => {
    setDraftRating(existing?.rating ?? null);
    setDraftNotes(existing?.notes ?? '');
  }, [existing?.id, existing?.rating, existing?.notes]);

  if (!userId) return null;

  const savedRating = existing?.rating ?? null;
  const hasUnsavedChanges =
    draftRating !== null &&
    (draftRating !== savedRating || (draftNotes ?? '') !== (existing?.notes ?? ''));

  async function handleSubmit() {
    if (!userId || !draftRating) return;
    const row = await submitWeek({
      userId,
      weekStart,
      rating: draftRating,
      notes: draftNotes.trim() || undefined,
    });
    if (row) setExpanded(false);
  }

  async function handleRemove() {
    if (!existing) return;
    await remove(existing.id);
    setDraftRating(null);
    setDraftNotes('');
    setExpanded(false);
  }

  function pickRating(next: FeedbackRating) {
    setDraftRating(next);
    setExpanded(true);
  }

  return (
    <div className="mt-4 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Rate this week's structure</h4>
          <p className="text-xs text-gray-500">
            Is the day-to-day progression, intensity arc, and exercise rotation correct for this user?
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => pickRating('up')}
            disabled={isPending}
            title="Good weekly structure"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full border transition',
              draftRating === 'up'
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-300 bg-white text-gray-400 hover:border-gray-400 hover:text-gray-600',
            )}
          >
            <ThumbsUp className="h-4 w-4" />
          </button>
          <button
            onClick={() => pickRating('down')}
            disabled={isPending}
            title="Bad weekly structure"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full border transition',
              draftRating === 'down'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-300 bg-white text-gray-400 hover:border-gray-400 hover:text-gray-600',
            )}
          >
            <ThumbsDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {savedRating && !expanded && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-600">
            {savedRating === 'up' ? '👍 saved' : '👎 saved'}
            {existing?.notes ? ` · "${existing.notes.slice(0, 80)}${existing.notes.length > 80 ? '…' : ''}"` : ''}
          </span>
          <button
            onClick={() => setExpanded(true)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {existing?.notes ? 'Edit notes' : 'Add notes'}
          </button>
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-2">
          <textarea
            rows={3}
            placeholder={
              draftRating === 'up'
                ? "What's right about this weekly structure? (e.g. correct phase progression, intensity arc lands the deload, exercise rotation hits each pattern…)"
                : draftRating === 'down'
                  ? "What's wrong? (e.g. too many CNS-heavy days in a row, no recovery between leg sessions, exercise rotation misses a movement pattern…)"
                  : 'Pick 👍 or 👎 first, then add reasoning.'
            }
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            disabled={isPending || !draftRating}
            className="w-full resize-none rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-700 focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary disabled:bg-gray-50"
          />
          <div className="flex items-center justify-between gap-2">
            <div>
              {existing && (
                <button
                  onClick={handleRemove}
                  disabled={isPending}
                  className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setExpanded(false);
                  setDraftRating(existing?.rating ?? null);
                  setDraftNotes(existing?.notes ?? '');
                }}
                disabled={isPending}
                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!draftRating || !hasUnsavedChanges || isPending}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition',
                  draftRating && hasUnsavedChanges && !isPending
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-200 text-gray-400',
                )}
              >
                {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {existing ? 'Update week feedback' : 'Save week feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
